import { PeriodType } from '@shared/enums/PeriodType';
import { TagName } from '@shared/enums/TagName';

/**
 * Frontend-only helpers for Highlight IQ.
 *
 * Formula definitions and scoring live in asport-highlight-iq. The frontend
 * only uses the formula metadata returned by that service to build request
 * payloads and UI limits.
 */

export const getDefaultClipCount = (formulaConfig) => formulaConfig?.defaultClipCount || 10;

export const mergeFormulaConfig = (formulaCatalog, formulaConfig) => {
	if (!formulaConfig) {
		return null;
	}

	return {
		...(formulaCatalog?.baseConfig ?? {}),
		...formulaConfig,
	};
};

export const formulaConfigByValue = (formulaCatalog, formulaType) => {
	const formulaKey = typeof formulaType === 'object' && formulaType.value ? formulaType.value : formulaType;
	return formulaCatalog?.formulas?.find(formula => formula.value === formulaKey) ?? null;
};

/**
 * Check if teams have adequate distribution for team-based formulas
 * @param {Array} tags
 * @returns {Boolean}
 */
export const hasTeamDistribution = (tags) => {
	const teamIds = new Set(tags.map(tag => tag.teamId).filter(Boolean));
	return teamIds.size >= 2;
};

const sortByTimestampAsc = (a, b) => (a?.timestamp ?? 0) - (b?.timestamp ?? 0);

const getTagSelectionKey = (tag) => {
	if (tag?.id !== undefined && tag?.id !== null) {
		return `id:${tag.id}`;
	}

	return `t:${tag?.timestamp ?? -1}|n:${tag?.tag?.name ?? tag?.name ?? 'unknown'}|team:${tag?.teamId ?? 'none'}`;
};

const getPeriodMarkers = (tags, periodConfig, occurrenceIndex = 0) => {
	if (!periodConfig?.start || !periodConfig?.end) {
		return { start: null, end: null, usedFallback: false };
	}

	const sortedTags = [...tags].sort(sortByTimestampAsc);
	const startMarkers = sortedTags.filter(tag => tag.tag?.name === periodConfig.start);
	const endMarkers = sortedTags.filter(tag => tag.tag?.name === periodConfig.end);

	if (!startMarkers.length || !endMarkers.length) {
		return { start: null, end: null, usedFallback: false };
	}

	const startMarker = startMarkers[occurrenceIndex] || null;
	const endMarker = endMarkers[occurrenceIndex] || null;

	if (!startMarker || !endMarker || startMarker.timestamp > endMarker.timestamp) {
		for (const potentialStart of startMarkers) {
			const potentialEnd = endMarkers.find(end => end.timestamp > potentialStart.timestamp);
			if (potentialEnd) {
				return { start: potentialStart, end: potentialEnd, usedFallback: true };
			}
		}

		return { start: null, end: null, usedFallback: true };
	}

	return { start: startMarker, end: endMarker, usedFallback: false };
};

const getPeriodPairs = (tags, periodMarkers = []) => {
	return periodMarkers
		.map((periodConfig, index) => {
			const { start, end, usedFallback } = getPeriodMarkers(tags, periodConfig, index);
			if (!start || !end) {
				return null;
			}

			return {
				start,
				end,
				periodConfig,
				periodIndex: index,
				usedFallback,
			};
		})
		.filter(Boolean);
};

/**
 * Build action/break period ranges (seconds, same time base as tag timestamps) for Highlight IQ lambda metadata.
 */
export const buildHighlightIqMetadataPeriods = (allTags, mergedFormulaConfig, durationSeconds) => {
	const duration = Math.max(0, Math.floor(Number(durationSeconds) || 0));
	const periodMarkersToUse = mergedFormulaConfig?.periodMarkers?.length
		? mergedFormulaConfig.periodMarkers
		: [];
	const pairs = getPeriodPairs(allTags, periodMarkersToUse)
		.filter(pair => pair.start && pair.end && pair.start.timestamp <= pair.end.timestamp)
		.sort((a, b) => a.start.timestamp - b.start.timestamp);

	if (pairs.length === 0) {
		return duration > 0 ? [{ start: 0, end: duration, type: PeriodType.ACTION }] : [];
	}

	const periods = [];
	let cursor = 0;
	for (const pair of pairs) {
		const actionStart = pair.start.timestamp;
		const actionEnd = pair.end.timestamp;
		if (actionStart > cursor) {
			periods.push({ start: cursor, end: actionStart, type: PeriodType.BREAK });
		}
		periods.push({ start: actionStart, end: actionEnd, type: PeriodType.ACTION });
		cursor = Math.max(cursor, actionEnd);
	}
	if (cursor < duration) {
		periods.push({ start: cursor, end: duration, type: PeriodType.BREAK });
	}
	return periods;
};

const getGoalScores = (event) => {
	const teamHomeScore = Number.isFinite(event?.finalScore?.teamHomeScore)
		? event.finalScore.teamHomeScore
		: Number(event?.finalScore?.teamHomeScore ?? 0);
	const teamGuestScore = Number.isFinite(event?.finalScore?.teamGuestScore)
		? event.finalScore.teamGuestScore
		: Number(event?.finalScore?.teamGuestScore ?? 0);

	return {
		teamHomeScore:  Number.isFinite(teamHomeScore) ? teamHomeScore : 0,
		teamGuestScore: Number.isFinite(teamGuestScore) ? teamGuestScore : 0,
	};
};

const collectExtraTags = (tags, formulaConfig, event) => {
	if (!formulaConfig) return [];

	const extraTags = new Map();
	const addExtraTag = (tag, selectionReason) => {
		if (!tag) return;
		const key = getTagSelectionKey(tag);
		if (extraTags.has(key)) return;
		extraTags.set(key, { ...tag, selectionReason });
	};

	if (formulaConfig.includeExtraTags?.length) {
		formulaConfig.includeExtraTags.forEach(tagName => {
			const matchingTags = tags.filter(tag => tag.tag?.name === tagName);
			matchingTags.forEach(tag => {
				addExtraTag(tag, 'extra_tag');
			});
		});
	}

	if (formulaConfig.includeLastGoal) {
		const goalTagNames = [TagName.ACTION_GOAL, TagName.ACTION_PENALTY_GOAL, TagName.ACTION_OWN_GOAL, TagName.ACTION_7M_GOAL];
		const goalTags = tags.filter(tag => goalTagNames.includes(tag.tag?.name));
		if (goalTags.length) {
			const lastGoal = goalTags.reduce((latest, tag) =>
				tag.timestamp > (latest?.timestamp || -Infinity) ? tag : latest, null
			);
			if (lastGoal) {
				addExtraTag(lastGoal, 'last_goal');
			}
		}
	}

	const periodPairs = getPeriodPairs(tags, formulaConfig.periodMarkers ?? []);

	if ((formulaConfig.includeEventStart || formulaConfig.includeEventEnd) && periodPairs.length) {
		const firstPeriod = periodPairs[0];
		const lastPeriod = periodPairs[periodPairs.length - 1];

		if (firstPeriod?.start && formulaConfig.includeEventStart) {
			addExtraTag(firstPeriod.start, 'event_start');
		}
		if (lastPeriod?.end && formulaConfig.includeEventEnd) {
			addExtraTag(lastPeriod.end, 'event_end');
		}
	}

	const hasFinalScore = event?.finalScore &&
		event.finalScore.teamHomeScore !== null &&
		event.finalScore.teamHomeScore !== undefined &&
		event.finalScore.teamGuestScore !== null &&
		event.finalScore.teamGuestScore !== undefined;
	const { teamHomeScore, teamGuestScore } = getGoalScores(event);
	const gameEndedWithLessThan3GoalsDifference = hasFinalScore && Math.abs(teamHomeScore - teamGuestScore) < 3;
	if (formulaConfig.includeLastMinutesEvents?.enabled && gameEndedWithLessThan3GoalsDifference) {
		const { minutes, eventTypes } = formulaConfig.includeLastMinutesEvents;
		const minutesInSeconds = minutes * 60;
		const lastPeriod = periodPairs[periodPairs.length - 1];
		const lastPeriodEnd = lastPeriod?.end;

		if (lastPeriodEnd) {
			const lastMinutesStart = lastPeriodEnd.timestamp - minutesInSeconds;
			const lastMinutesTags = tags.filter(tag =>
				tag.timestamp >= lastMinutesStart &&
				tag.timestamp <= lastPeriodEnd.timestamp &&
				eventTypes.includes(tag.tag?.name)
			);

			lastMinutesTags.forEach(tag => {
				addExtraTag(tag, 'last_minutes');
			});
		}
	}

	return Array.from(extraTags.values()).sort(sortByTimestampAsc);
};

export const calculateExtraTagsCount = (tags, formulaConfig, _sportName, event) => {
	return collectExtraTags(tags, formulaConfig, event).length;
};
